import { useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "../lib/api";
import { notifyQuotaExhausted } from "../lib/quota";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { MarkdownText } from "../components/MarkdownText";
import { CopyReadingButton } from "../components/CopyReadingButton";
import { DeleteReadingButton } from "../components/DeleteReadingButton";
import { FiguraRiconosciuta, FiguraInvite } from "../components/FiguraRiconosciuta";
import { MoonStar, Sparkles } from "lucide-react";

// L'Oracolo del Destino: tre fasi, procedimento interamente invisibile al consultante.
export default function Oracolo() {
  const { user, loading } = useAuth();
  const [phase, setPhase] = useState("ask"); // ask | wait | reading | done
  const [question, setQuestion] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [text, setText] = useState("");
  const [readingId, setReadingId] = useState(null);
  const [figura, setFigura] = useState(null);
  const [figuraOpen, setFiguraOpen] = useState(false);
  const [error, setError] = useState("");
  const [starting, setStarting] = useState(false);

  const start = async () => {
    setError("");
    setStarting(true);
    try {
      const { data } = await api.post("/oracolo/start", { question });
      setSessionId(data.id);
      setPhase("wait");
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "quota_esaurita") notifyQuotaExhausted();
      else if (d?.code === "review_required") toast.error(d.message);
      else setError(typeof d === "string" ? d : "Qualcosa è andato storto. Riprova.");
    } finally {
      setStarting(false);
    }
  };

  const consult = async () => {
    setPhase("reading");
    setText("");
    setError("");
    try {
      const res = await fetch(`${API}/oracolo/${sessionId}/consult`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        const det = d.detail;
        if (det?.code === "quota_esaurita") { notifyQuotaExhausted(); setPhase("wait"); return; }
        throw new Error(typeof det === "string" ? det : "Qualcosa è andato storto. Riprova.");
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const line = p.trim();
          if (!line.startsWith("data: ")) continue;
          const ev = JSON.parse(line.slice(6));
          if (ev.type === "meta") { setReadingId(ev.reading_id); setFigura(ev.figura || null); }
          else if (ev.type === "delta") setText((t) => t + ev.content);
          else if (ev.type === "error") throw new Error(ev.content);
        }
      }
      setPhase("done");
    } catch (e) {
      setError(e.message);
      setPhase("done");
    }
  };

  const reset = () => {
    setPhase("ask");
    setQuestion("");
    setSessionId(null);
    setText("");
    setReadingId(null);
    setFigura(null);
    setFiguraOpen(false);
    setError("");
  };

  if (loading) return null;

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-4 py-24 text-center" data-testid="oracolo-login-required">
        <div className="mystic-card p-10">
          <img src="/oracolo-strega.png" alt="L'Oracolo" className="w-20 h-20 object-contain mx-auto mb-5" style={{ filter: "drop-shadow(0 0 14px rgba(245,215,127,0.35))" }} data-testid="oracolo-strega-login" />
          <h1 className="font-display text-3xl text-amber-100 mb-4">L'Oracolo del Destino</h1>
          <p className="text-purple-200/70 text-sm mb-8">Per consultare l'Oracolo è necessario accedere o creare un account.</p>
          <Link to="/accedi?redirect=/oracolo" data-testid="oracolo-login-btn" className="gold-btn inline-block px-8 py-3 text-sm">
            Accedi o registrati
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 lg:px-8 py-16 sm:py-20" data-testid="oracolo-page">
      {phase === "ask" && (
        <div className="text-center" data-testid="oracolo-ask">
          <h1 className="font-display text-4xl sm:text-5xl text-amber-100">L'Oracolo del Destino</h1>
          <p className="font-cinzel text-amber-300/80 text-xs tracking-[0.25em] uppercase mt-4">Ogni domanda apre una possibilità</p>
          <div className="mystic-card p-7 sm:p-9 mt-10 text-left space-y-6">
            <p className="text-purple-200/80 text-sm text-center">Che cosa vuoi sapere?</p>
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              maxLength={600}
              placeholder="Scrivi qui la tua domanda…"
              data-testid="oracolo-question-input"
              className="input-dark min-h-[110px] resize-y"
            />
            {error && <p className="text-red-400 text-sm" data-testid="oracolo-error">{error}</p>}
            <button
              onClick={start}
              disabled={question.trim().length < 5 || starting}
              data-testid="oracolo-consulta-btn"
              className="gold-btn w-full py-3.5 text-sm flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Sparkles className="w-4 h-4" /> {starting ? "Un attimo…" : "CONSULTA L'ORACOLO"}
            </button>
          </div>
        </div>
      )}

      {phase === "wait" && (
        <div className="text-center" data-testid="oracolo-wait">
          <h1 className="font-display text-4xl sm:text-5xl text-amber-100">L'Oracolo del Destino</h1>
          <p className="font-cinzel text-amber-300/80 text-xs tracking-[0.25em] uppercase mt-4">Ogni domanda apre una possibilità</p>
          <div className="mystic-card p-10 sm:p-12 mt-10 space-y-8">
            <img src="/oracolo-strega.png" alt="L'Oracolo" className="w-16 h-16 object-contain mx-auto" style={{ filter: "drop-shadow(0 0 12px rgba(245,215,127,0.3))" }} data-testid="oracolo-strega-wait" />
            <p className="text-purple-200/80 text-base" data-testid="oracolo-wait-text">Prenditi il tuo tempo.</p>
            <button
              onClick={consult}
              data-testid="oracolo-inizia-btn"
              className="gold-btn px-10 py-3.5 text-sm"
            >
              INIZIA LA CONSULTAZIONE
            </button>
          </div>
        </div>
      )}

      {(phase === "reading" || phase === "done") && (
        <div data-testid="oracolo-result">
          <div className="text-center mb-10">
            <img src="/oracolo-strega.png" alt="L'Oracolo" className="w-20 h-20 sm:w-24 sm:h-24 object-contain mx-auto mb-5 animate-pulse" style={{ filter: "drop-shadow(0 0 16px rgba(245,215,127,0.4))" }} data-testid="oracolo-strega-responso" />
            <h1 className="font-display text-4xl sm:text-5xl text-amber-100">L'Oracolo del Destino</h1>
            <p className="font-cinzel text-amber-300/80 text-xs tracking-[0.25em] uppercase mt-4">Ogni domanda apre una possibilità</p>
          </div>
          <div className="mystic-card p-7 sm:p-9" data-testid="oracolo-responso-card">
            <p className="text-sm text-purple-300/60 italic mb-6 text-center">“{question}”</p>
            <div className="text-purple-100/85 leading-loose text-sm sm:text-base whitespace-pre-line" data-testid="oracolo-responso">
              <MarkdownText text={text} />
              {phase === "reading" && <span className="inline-block w-2 h-4 bg-amber-400/70 ml-1 animate-pulse" />}
            </div>
            {phase === "reading" && !text && (
              <p className="text-purple-300/50 text-sm" data-testid="oracolo-thinking">L'Oracolo sta formulando il responso…</p>
            )}
            {error && <p className="text-red-400 text-sm mt-2" data-testid="oracolo-error">{error}</p>}
            {phase === "done" && text && (
              <div className="mt-6 flex justify-center gap-2 flex-wrap">
                <CopyReadingButton text={text} testid="copy-reading-oracolo" />
                {readingId && (
                  <DeleteReadingButton testid="delete-reading-oracolo" onDelete={async () => {
                    await api.delete(`/readings/${readingId}`);
                    toast.success("Responso eliminato.");
                    reset();
                  }} />
                )}
              </div>
            )}
            {phase === "done" && text && figura && (
              <FiguraInvite onOpen={() => setFiguraOpen(true)} onDismiss={() => setFigura(null)} />
            )}
            {figuraOpen && figura && (
              <FiguraRiconosciuta figura={figura} readingId={readingId} onClose={() => { setFiguraOpen(false); setFigura(null); }} />
            )}
            {phase === "done" && text && (
              <div className="mt-6 pt-5 border-t border-amber-500/15 text-center">
                <button onClick={reset} data-testid="oracolo-new-btn" className="text-xs text-amber-300/80 hover:text-amber-200 transition-colors">
                  Fai un'altra domanda
                </button>
              </div>
            )}
          </div>
          {phase === "done" && (
            <p className="text-xs text-purple-300/50 text-center leading-relaxed mt-6">
              Il responso ha finalità di riflessione e orientamento personale. Eventuali tempistiche indicate
              sono puramente indicative e non costituiscono una certezza.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
